import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
  PropsWithChildren,
} from 'react'
import {
  View,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  RefreshControl,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native'

import { NetworkStatus } from '@apollo/client'
import { useNavigation } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PdfControls } from './components/PdfControls'
import { PdfView } from './components/PdfView'
import {
  PreviewFallback,
  PaperPagePlaceholder,
} from './components/PreviewFallback'
import { PreviewRegenerateConfirmationModal } from './components/PreviewRegenerateConfirmationModal'
import {
  Ebook,
  useGetEBooksQuery,
  useGenerateFullEbookMutation,
  useUpdateEbookMutation,
} from '../../api/types'
import Button from '../../components/Button'
import HeaderButton from '../../components/HeaderButton'
import HeaderIconButton from '../../components/HeaderIconButton'
import Loading from '../../components/Loading'
import PickerModal from '../../components/PickerModal'
import Separator from '../../components/Separator'
import Typography from '../../components/Text/Typography'
import TouchableSvg from '../../components/TouchableSvg'
import logger from '../../core/logging-core'
import { nativeDownloadFile } from '../../helpers/nativeDownloadFile'
import useNativeShare from '../../hooks/useNativeShare.web'
import useSafeAreaPaddedStyle, {
  headerOptions,
} from '../../hooks/useSafeAreaPaddedStyle'
import { useWebLayout } from '../../providers/WebLayoutProvider'
import appStyles from '../../styles/app-styles'
import Colors from '../../styles/Colors'
import { MainStackNavigationType } from '../../types/navigation-types'
import { PdfViewHandle, PdfViewConfig } from '../../types/PdfView.types'
import { convertHexToRGBA } from '../../utils/colors'

const POLLING_INTERVAL = 5000
const INLINE_FOOTER_WIDTH_THRESHOLD = 700
const INITIAL_NAVIGATE_TO_SETTINGS_DELAY = 2000

export default function EBookPreviewScreen(): JSX.Element {
  const insets = useSafeAreaInsets()
  const nativeShare = useNativeShare()
  const { webLayoutEnabled, width } = useWebLayout()
  const { navigate, goBack } =
    useNavigation<MainStackNavigationType<'EBookPreview'>>()
  const { t, i18n } = useTranslation(undefined, {
    keyPrefix: 'screens.ebookPreview',
  })

  const pdfViewRef = useRef<PdfViewHandle | null>(null)

  const [page, setPage] = useState<number>(1)
  const [isDownloading, setIsDownloading] = useState(false)
  const [pdfViewConfig, setPdfViewConfig] = useState<PdfViewConfig>()
  const [isFullNeededModalVisible, setIsFullNeededModalVisible] =
    useState(false)
  const [
    isPreviewRegenerateConfimationModalVisible,
    setIsPreviewRegenerateConfimationModalVisible,
  ] = useState(false)

  const { data, loading, refetch, stopPolling, startPolling, networkStatus } =
    useGetEBooksQuery({
      fetchPolicy: 'network-only',
    })
  const ebook: Ebook | undefined = data?.ebooks?.nodes?.[0]
  const isGeneratingPreview =
    !!ebook && ebook.beingGenerated && !ebook.previewFileUrl
  const isGeneratingFullEbook =
    !isGeneratingPreview && ebook?.beingGenerated && !ebook.fullFileUrl

  const pdfUrl = ebook?.fullFileUrl ?? ebook?.previewFileUrl

  const [updateEbookMutation] = useUpdateEbookMutation({
    onCompleted: () => refetch(),
  })
  const [generateFullEbookMutation] = useGenerateFullEbookMutation({
    onCompleted: () => refetch(),
  })

  // TODO: Consider a status message instead
  const hasSeenEdit = useRef(false)
  useEffect(() => {
    if (ebook && !ebook.title && !ebook.intro && !hasSeenEdit.current) {
      const timeout = setTimeout(() => {
        if (!hasSeenEdit.current) {
          hasSeenEdit.current = true
          navigate('EBookSettings', { ebookId: ebook.id })
        }
      }, INITIAL_NAVIGATE_TO_SETTINGS_DELAY)

      return () => clearTimeout(timeout)
    }
  }, [ebook, navigate])

  const shouldPoll = !ebook || ebook.beingGenerated
  useEffect(
    function pollEbookWhileGenerating() {
      if (shouldPoll) {
        startPolling(POLLING_INTERVAL)
        return stopPolling
      }

      stopPolling()
    },
    [shouldPoll, startPolling, stopPolling],
  )

  const onRequestCloseModal = useCallback(
    () => setIsFullNeededModalVisible(false),
    [],
  )

  const onGenerateFullPress = useCallback(() => {
    setIsFullNeededModalVisible(false)
    ebook &&
      generateFullEbookMutation({ variables: { id: ebook.id } }).catch(
        logger.error,
      )
  }, [ebook, generateFullEbookMutation])

  const onRequestCloseRegeneratePreviewModal = useCallback(() => {
    setIsPreviewRegenerateConfimationModalVisible(false)
  }, [])

  const onRegeneratePreviewConfirmed = useCallback(() => {
    if (!ebook) return

    // NOTE: POST update with empty input to trigger regen
    updateEbookMutation({
      variables: {
        id: ebook.id,
        input: {},
      },
    }).then(() => {
      setIsPreviewRegenerateConfimationModalVisible(false)
    })
  }, [ebook, updateEbookMutation])

  const onRegeneratePreviewPress = useCallback(() => {
    if (isGeneratingPreview) return

    // NOTE: no need to confirm if full ebook isn't generated
    if (!ebook?.fullFileUrl && !isGeneratingFullEbook) {
      return onRegeneratePreviewConfirmed()
    }

    setIsPreviewRegenerateConfimationModalVisible(true)
  }, [
    ebook?.fullFileUrl,
    isGeneratingPreview,
    isGeneratingFullEbook,
    onRegeneratePreviewConfirmed,
  ])

  const withFull = useCallback(
    (action: (ebook: Ebook & { fullFileUrl: string }) => void) => {
      if (!ebook?.fullFileUrl) {
        setIsFullNeededModalVisible(true)
      } else {
        action({ ...ebook, fullFileUrl: ebook.fullFileUrl! })
      }
    },
    [ebook],
  )

  const onOderOrderPhysicalPress = useCallback(() => {
    withFull(({ id }) => navigate('EBookRequestPrintout', { ebookId: id }))
  }, [navigate, withFull])

  const onSharePress = useCallback(() => {
    withFull(fullEbook => {
      nativeShare(fullEbook.fullFileUrl, fullEbook)
    })
  }, [withFull, nativeShare])

  const onDownloadPress = useCallback(() => {
    withFull(fullEbook => {
      setIsDownloading(true)
      nativeDownloadFile(
        fullEbook.fullFileUrl,
        fullEbook.fullFileFilename || 'ebook.pdf',
      ).finally(() => setIsDownloading(false))
    })
  }, [withFull])

  const onEditPress = useCallback(() => {
    const ebookId = ebook?.id
    if (!ebookId) return console.error('no ebook id!', ebook)

    navigate('EBookSettings', { ebookId })
  }, [navigate, ebook])

  const changePdfPage = useCallback((pageNumber: number) => {
    pdfViewRef.current?.setPage(pageNumber)
  }, [])

  const renderOrderPhysicalBanner = useCallback(
    () => (
      <Pressable
        style={({ pressed }) => [
          styles.orderPhysicalBanner,
          pressed && styles.orderPhysicalBannerPressed,
        ]}
        onPress={onOderOrderPhysicalPress}>
        <PreviewFallback
          maxWidth={33}
          contentColor={Colors['layout.light-gray']}
          backgroundColor={Colors['layout.gray']}
        />
        <Separator width={19} />
        <Typography weight="medium" style={styles.orderPhysicalBannerText}>
          {t('orderPhysical')}
        </Typography>
      </Pressable>
    ),
    [t, onOderOrderPhysicalPress],
  )

  const renderStatusMessage = useCallback(() => {
    if (isGeneratingPreview) {
      return (
        <View style={appStyles.inlineContainer}>
          <ActivityIndicator color={Colors['brand.action']} />
          <Separator width={10} />
          <View style={appStyles.fullSize}>
            <Typography weight="medium" style={styles.statusMessageTitle}>
              {t('status.generatingPreview.title')}
            </Typography>
            <Separator height={5} />
            <Typography>{t('status.generatingPreview.description')}</Typography>
          </View>
        </View>
      )
    }

    if (ebook?.previewFileUrl && !ebook.fullFileUrl) {
      if (ebook.beingGenerated) {
        return (
          <View style={appStyles.inlineContainer}>
            <ActivityIndicator color={Colors['brand.action']} />
            <Separator width={10} />
            <View style={appStyles.fullSize}>
              <Typography weight="medium" style={styles.statusMessageTitle}>
                {t('status.generatingFull.title')}
              </Typography>
              <Separator height={5} />
              <Typography>{t('status.generatingFull.description')}</Typography>
            </View>
          </View>
        )
      }

      return (
        <View>
          <Typography>{t('status.previewReady.generateFull')}</Typography>
          <Separator height={10} />
          <Button
            type="secondary"
            style={styles.generateFullButton}
            text={t('status.previewReady.generateFullButton')}
            onPress={onGenerateFullPress}
          />
        </View>
      )
    }
  }, [t, ebook, isGeneratingPreview, onGenerateFullPress])

  const isFooterInline = useMemo(
    () => width > INLINE_FOOTER_WIDTH_THRESHOLD,
    [width],
  )

  const renderFooter = useCallback(() => {
    const ControlsElement = !!pdfUrl && (
      <PdfControls
        pageNumber={page}
        style={styles.pdfControls}
        step={pdfViewConfig?.step ?? 1}
        pageCount={pdfViewConfig?.numberOfPages}
        setPageNumber={setPage}
        changePdfPage={changePdfPage}
      />
    )
    const StatusMessageElement = renderStatusMessage()

    const showSeparator =
      !!StatusMessageElement && !!ControlsElement && !isFooterInline
    return (
      <View
        style={[
          styles.footer,
          isFooterInline && styles.inlineFooter,
          { paddingBottom: insets.bottom + 20 },
        ]}>
        {ControlsElement}
        {isFooterInline && <View style={appStyles.fullSize} />}
        {showSeparator && <View style={styles.footerDivider} />}
        {StatusMessageElement}
      </View>
    )
  }, [
    page,
    pdfUrl,
    setPage,
    insets.bottom,
    pdfViewConfig,
    changePdfPage,
    isFooterInline,
    renderStatusMessage,
  ])

  return (
    <Container>
      <Loading loading={loading && !ebook} />
      <View style={useSafeAreaPaddedStyle(styles.header, headerOptions)}>
        <View style={appStyles.inlineContainer}>
          <TouchableSvg name="back" color="layout.dark" onPress={goBack} />
        </View>
        <View style={appStyles.inlineContainer}>
          <HeaderIconButton
            iconName="refresh"
            text={webLayoutEnabled && width > 950 && t('regenerate')}
            onPress={onRegeneratePreviewPress}
          />
          <Separator width={10} />

          {webLayoutEnabled && (
            <View style={styles.orderPhysicalHeaderButtonHolder}>
              <HeaderButton
                secondary
                text={t('orderPhysical')}
                onPress={onOderOrderPhysicalPress}
              />
            </View>
          )}
          <HeaderIconButton
            iconName="share"
            text={webLayoutEnabled && t('share')}
            onPress={onSharePress}
          />
          <Separator width={10} />
          <HeaderIconButton
            iconName="download"
            isLoading={isDownloading}
            text={webLayoutEnabled && i18n.t('common.download')}
            onPress={onDownloadPress}
          />
          <Separator width={10} />
          <HeaderIconButton
            iconName="pencil"
            text={webLayoutEnabled && i18n.t('common.edit')}
            onPress={onEditPress}
          />
        </View>
      </View>

      {isGeneratingPreview ? (
        <ScrollView
          refreshControl={
            <RefreshControl
              refreshing={networkStatus === NetworkStatus.refetch}
              onRefresh={refetch}
            />
          }
          style={appStyles.fullSize}
          contentContainerStyle={styles.scrollContentContainer}>
          <Separator height={19} />
          {renderOrderPhysicalBanner()}
          <Separator height={14} />
          <View style={webLayoutEnabled && styles.webPreviewFallbackContainer}>
            <PreviewFallback maxWidth={500} />
            <Separator height={13} width={13} />
            <PaperPagePlaceholder maxWidth={500} />
          </View>
          <Separator height={20} />
        </ScrollView>
      ) : (
        <View style={appStyles.fullSize}>
          {!!pdfUrl && (
            <PdfView
              ref={pdfViewRef}
              pdfUrl={pdfUrl}
              onPageChanged={setPage}
              onConfigurationChange={setPdfViewConfig}
            />
          )}
          {!webLayoutEnabled && (
            <View style={styles.floatingBannerHolder}>
              {renderOrderPhysicalBanner()}
            </View>
          )}
        </View>
      )}
      {renderFooter()}
      <Modal
        transparent
        statusBarTranslucent
        animationType="fade"
        visible={isFullNeededModalVisible}
        onRequestClose={onRequestCloseModal}>
        <PickerModal close={onRequestCloseModal}>
          <View style={styles.modalContentContainer}>
            <Separator height={20} />
            <View style={styles.modalIconHolder}>
              <PreviewFallback
                maxWidth={200}
                contentColor={Colors['layout.light']}
                backgroundColor={Colors['layout.gray']}
              />
            </View>
            <Separator height={10} />
            <Typography weight="medium" style={styles.modalTitle}>
              {t('fullFileNeededModal.title')}
            </Typography>
            <Separator height={5} />
            <Typography style={styles.modalMessage}>
              {t(
                `fullFileNeededModal.message.${
                  isGeneratingFullEbook ? 'inProgress' : 'generate'
                }`,
              )}
            </Typography>
            <Separator height={50} />
            {!isGeneratingFullEbook && (
              <>
                <Button
                  text={t('fullFileNeededModal.generateButton')}
                  onPress={onGenerateFullPress}
                />
                <Separator height={10} />
              </>
            )}
            <Button
              type="secondary"
              text={i18n.t(
                `common.${isGeneratingFullEbook ? 'continue' : 'cancel'}`,
              )}
              onPress={onRequestCloseModal}
            />
            <Separator height={insets.bottom} />
          </View>
        </PickerModal>
      </Modal>
      <PreviewRegenerateConfirmationModal
        isVisible={isPreviewRegenerateConfimationModalVisible}
        onConfirmPress={onRegeneratePreviewConfirmed}
        onRequestClose={onRequestCloseRegeneratePreviewModal}
      />
    </Container>
  )
}

function Container({ children }: PropsWithChildren) {
  const { height } = useWindowDimensions()
  const { top, bottom } = useSafeAreaInsets()
  // NOTE: maxHeight is required for sticky footer in web
  const maxHeight = height + top + bottom
  return <View style={[styles.container, { maxHeight }]}>{children}</View>
}

const FOOTER_PADDING = 24
const borderColor = convertHexToRGBA(Colors['layout.gray'], 0.5)
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors['layout.white'],
  },
  header: {
    borderColor,
    paddingTop: 15,
    paddingBottom: 16,
    borderBottomWidth: 1,
    paddingHorizontal: 24,
    ...appStyles.inlineContainer,
    justifyContent: 'space-between',
    backgroundColor: Colors['layout.white'],
  },
  orderPhysicalHeaderButtonHolder: { paddingRight: 10 },
  scrollContentContainer: {
    flexGrow: 1,
    paddingHorizontal: 13,
  },
  orderPhysicalBanner: {
    ...appStyles.inlineContainer,
    width: '100%',
    paddingTop: 11,
    borderRadius: 10,
    paddingBottom: 10,
    paddingHorizontal: 19,
    backgroundColor: Colors['layout.light-gray'],
  },
  orderPhysicalBannerPressed: {
    backgroundColor: Colors['layout.gray'],
  },
  orderPhysicalBannerText: { fontSize: 16 },
  floatingBannerHolder: {
    top: -11,
    left: 12,
    right: 12,
    position: 'absolute',
  },
  webPreviewFallbackContainer: {
    ...appStyles.row,
    ...appStyles.center,
  },
  footer: {
    padding: FOOTER_PADDING,
    width: '100%',
    borderTopWidth: 1,
    opacity: 0.5,
    backgroundColor: Colors['layout.white'],
    borderColor,
  },
  inlineFooter: { flexDirection: 'row-reverse' },
  footerDivider: {
    height: 1,
    marginVertical: 10,
    backgroundColor: borderColor,
    marginHorizontal: -FOOTER_PADDING,
  },
  pdfControls: { justifyContent: 'flex-end' },
  statusMessageTitle: { fontSize: 16 },
  generateFullButton: { alignSelf: 'flex-start' },
  modalContentContainer: {
    ...appStyles.horizontalCenter,
    padding: 20,
  },
  modalIconHolder: {
    width: 100,
    aspectRatio: Math.SQRT1_2,
  },
  modalTitle: {
    fontSize: 20,
  },
  modalMessage: {
    fontSize: 16,
    textAlign: 'center',
  },
})
